import type { TokenContext } from '@ori/auth';
import type { ClientServiceUrls } from '@ori/configuration-manager';
import type { EnvironmentContext } from '@ori/environment';
import { PbsCookieNames } from '@ori/personal-beauty-store';
import { ApiClient, getTenantCountry } from '@ori/static-api-client-react';
import Cookies from 'js-cookie';
import capitalize from 'lodash.capitalize';

import { getCountryCode } from './getCountryCode';
import { getHomeCountryUrl } from './getHomeCountryUrl';
import { getHiddenByUser } from './localStorageUtils';

export interface GetGeolocationDataParams
  extends Required<Pick<ClientServiceUrls, 'staticApiUrl'>>,
    EnvironmentContext,
    Pick<TokenContext, 'isAnonymousUser'> {
  /** When enabled is true other conditions are checked whether geolocation popup can be visible. */
  enabled: boolean;
}

const formatCountryName = (name: string) => name.split(' ').map(capitalize).join(' ');

export interface GetGeolocationData {
  /** If true dialog will appear. */
  visible: boolean;
  /** Name of the home country (where user is according to countryCode). */
  homeCountry: string;
  /** Url to the home country Oriflame. */
  homeCountryUrl: string;
  /** Name of the foreign country (where user is NOT according to countryCode). */
  foreignCountry: string;
}

/**
 * Fetches data needed to decide visibility of geolocation popup and data for homeCountry
 * (where user is according to countryCode) and foreignCountry (where user is NOT according to countryCode).
 * @param params - Parameters needed to fetch geolocation data.
 * @returns Promise with visibility and data for homeCountry and foreignCountry.
 */
export const getGeolocationData = async ({
  enabled,
  isAnonymousUser,
  staticApiUrl,
  tenant,
  environment = 'live',
}: GetGeolocationDataParams): Promise<GetGeolocationData> => {
  const countryCode = getCountryCode();
  if (!enabled || !isAnonymousUser || !countryCode || !tenant || countryCode === tenant) {
    return { foreignCountry: '', homeCountry: '', homeCountryUrl: '', visible: false };
  }
  const store = Cookies.get(PbsCookieNames.PBS_CODE) ?? Cookies.get(PbsCookieNames.PBS_OWNER);
  const hiddenByUser = getHiddenByUser();
  if (!store || hiddenByUser) {
    return { foreignCountry: '', homeCountry: '', homeCountryUrl: '', visible: false };
  }
  const { language } = navigator;
  const client = new ApiClient({ baseUrl: staticApiUrl, language, tenant });
  const [
    {
      data: { name: homeCountry = '' },
    },
    {
      data: { isForeignRegistrationAllowed: visible, name: foreignCountry = '' },
    },
  ] = await Promise.all([
    // We need to fetch translations with home country tenant (countryCode) to get correct translations.
    getTenantCountry(client, countryCode, countryCode, { lang: language }),
    getTenantCountry(client, countryCode, tenant, { lang: language }),
  ]);

  return {
    foreignCountry: formatCountryName(foreignCountry),
    homeCountry: formatCountryName(homeCountry),
    homeCountryUrl: getHomeCountryUrl({ environment, store }),
    visible,
  };
};
